import React, { createContext, useContext, useState } from "react";
import { useTranslation } from "react-i18next";

interface UploadContextProps {
    abortController: AbortController;
    uploadStarted: boolean;
    uploadProgress: number;
    uploadedFileIds: string[];
    selectedFiles: File[];
    setUploadStarted: React.Dispatch<React.SetStateAction<boolean>>;
    setUploadProgress: React.Dispatch<React.SetStateAction<number>>;
    setUploadedFileIds: React.Dispatch<React.SetStateAction<string[]>>;
    setSelectedFiles: React.Dispatch<React.SetStateAction<File[]>>;
    cancelUpload: () => void;
}

const UploadContext = createContext<UploadContextProps | undefined>(undefined);

export const UploadProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [abortController, setAbortController] = useState(new AbortController());
    const [uploadStarted, setUploadStarted] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [uploadedFileIds, setUploadedFileIds] = useState<string[]>([]);
    const [selectedFiles, setSelectedFiles] = useState<File[]>([]);

    const cancelUpload = () => {
        abortController.abort();
        setUploadProgress(0);
        setUploadStarted(false);
        setSelectedFiles([]);
        setAbortController(new AbortController());
    };

    return (
        <UploadContext.Provider
            value={{
                abortController,
                uploadStarted,
                uploadProgress,
                uploadedFileIds,
                selectedFiles,
                setUploadStarted,
                setUploadProgress,
                setUploadedFileIds,
                setSelectedFiles,
                cancelUpload,
            }}
        >
            {children}
        </UploadContext.Provider>
    );
};

export const useUploadContext = () => {
    const context = useContext(UploadContext);
    const { t } = useTranslation(["common"]);

    if (!context) {
        throw new Error(t("common:section.uploadProvider.error.uploadFailed"));
    }
    return context;
};
